import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './Table.scss';

/**
 * @deprecated Use `ContentTable` for new content. `Table` is kept so existing PolyC blocks
 * keep rendering.
 * @polyDeprecated true
 * @polySemanticUsage Deprecated: do not select this block for new content; existing pages keep
 * rendering. For tables, use ContentTable instead.
 */
export interface TableProps {
  /**
   * Optional heading shown above the table.
   * @polyType Text
   * @polyLabel Title
   * @polyHelp Optional heading shown above the table
   */
  title?: string;

  /**
   * Optional intro paragraph shown between the title and the table.
   * Supports inline markdown.
   * @polyType RichText
   * @polyLabel Description
   * @polyHelp Optional intro paragraph above the table
   */
  description?: string;

  /**
   * Markdown table content. The entire table as one string, including
   * the header row, alignment row, and body rows. Inline markdown
   * (**bold**, *italic*, [links](#), ~~strikethrough~~) is supported in
   * any cell.
   *
   * Example:
   *   | Plan | Price | Listings |
   *   | :--- | ---:  | :---:    |
   *   | Individual | £0.75/sale | Unlimited |
   *
   * @polyType RichText
   * @polyLabel Table content
   * @polyHelp Pipe-delimited markdown table (GFM). First row is headers, second row sets per-column alignment (:--- left, ---: right, :---: center).
   */
  tableContent: string;

  /**
   * Whether to display alternating row backgrounds.
   * @polyType Boolean
   * @polyLabel Zebra striping
   * @polyDefault false
   */
  isZebraStriped?: boolean;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

/** @deprecated Use `ContentTable` for new content. */
export const Table: React.FC<TableProps> = ({
  title,
  description,
  tableContent,
  isZebraStriped = false,
  anchorId,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Post-render validation: if remark-gfm didn't produce a <table>,
  // the markdown was malformed. Flag it so monitors/QA can detect.
  useEffect(() => {
    if (!scrollRef.current) return;
    const hasTable = scrollRef.current.querySelector('table');
    if (!hasTable) {
      scrollRef.current.closest('.table')?.setAttribute('data-poly-error', 'malformed-table');
      if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console -- author guidance during local dev
        console.warn('[Table] tableContent did not produce a valid table. Check GFM syntax.');
      }
    }
  }, [tableContent]);

  if (!tableContent?.trim()) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- author guidance during local dev
      console.warn('[Table] tableContent is empty or missing. Skipping render.');
    }
    return (
      <section className="table" data-poly-error="empty-table" aria-hidden="true" />
    );
  }

  return (
    <section className="table" data-variant={isZebraStriped ? 'zebra' : undefined} {...blockAnchorAttributes(anchorId)}>
      <div className="table__inner">
        {(title || description) && (
          <header className="table__header">
            {title && <h2 className="table__title">{title}</h2>}
            {description && (
              <div className="table__description">
                <ReactMarkdown>{description}</ReactMarkdown>
              </div>
            )}
          </header>
        )}

        <div className="table__scroll-wrap" ref={scrollRef}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: ({ children }) => (
                <table className="table__table">{children}</table>
              ),
              thead: ({ children }) => <thead>{children}</thead>,
              tbody: ({ children }) => <tbody>{children}</tbody>,
              tr: ({ children }) => <tr>{children}</tr>,
              th: ({ children, style }) => (
                <th scope="col" data-align={style?.textAlign || 'left'}>
                  {children}
                </th>
              ),
              td: ({ children, style }) => (
                <td data-align={style?.textAlign || 'left'}>
                  {children}
                </td>
              ),
              // Unwrap <p> tags that remark inserts inside cells
              p: ({ children }) => <>{children}</>,
            }}
          >
            {tableContent}
          </ReactMarkdown>
        </div>
      </div>
    </section>
  );
};
